import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import {
  ACTOR_USER_ID,
  FAVORITES_COLLECTION_ID,
} from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import {
  actorSchema,
  type Collection,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/**
 * Return the Favorites collection, creating it when it does not exist yet.
 *
 * Favorites is a singleton by its id: it is always `FAVORITES_COLLECTION_ID`, owned by the one
 * `user` row (`ACTOR_USER_ID`). Two devices, two tabs or two taps that create it therefore create
 * the same row, so there is never a duplicate to merge and never an owner to repair.
 *
 * It is created lazily, by the first "add to Favorites". Opening the app must not write to the
 * database: an eager create gave every fresh profile a `collection` row before the user did
 * anything, which made `hasLocalStudyData` report study data for someone who had studied nothing.
 */
export async function ensureFavoritesCollection(actorDb: ActorDatabase): Promise<Collection> {
  return actorDb(import.meta.location).transaction(async (tx) => {
    // A live Favorites row is the collection, whatever its id (a row from an old export, say).
    const existing = await tx.query.collection.findFirst({ where: { isFavorites: true } });
    if (existing !== undefined) {
      return existing;
    }
    const nowIso = new Date().toISOString();
    // The collection references its owner, so the `user` row comes first.
    const user = await tx.query.user.findFirst({ columns: { id: true } });
    if (user === undefined) {
      await createOrReviveActorRow(tx, actorSchema.user, { createdAt: nowIso, id: ACTOR_USER_ID });
    }
    await createOrReviveActorRow(tx, actorSchema.collection, {
      createdAt: nowIso,
      id: FAVORITES_COLLECTION_ID,
      isFavorites: true,
      name: "Favorites",
      userId: user?.id ?? ACTOR_USER_ID,
    });
    const favorites = await tx.query.collection.findFirst({
      where: { id: FAVORITES_COLLECTION_ID },
    });
    if (favorites === undefined) {
      throw new Error("The Favorites collection was not written");
    }
    return favorites;
  });
}
